import { useState } from 'react';
import { Users, Mic, MicOff, Video, VideoOff, Shield, Hand, UserX, Volume2, LogOut, HandMetal, Gauge } from 'lucide-react';
import { useParticipantsStore } from '../../stores/participantsStore';
import { useRoom } from '../../contexts/RoomContext';
import ConfirmModal from '../common/ConfirmModal';
import toast from 'react-hot-toast';

interface ParticipantsListProps {
  onClose?: () => void;
}

const ParticipantsList = ({ onClose }: ParticipantsListProps) => {
  const { participants, localParticipant } = useParticipantsStore();
  const { muteParticipant, muteAllParticipants, makeAdmin, kickUser, lowerParticipantHand, endMeeting, bitrateLevel, setBitrateLevel } = useRoom();
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const allParticipants = [
    localParticipant,
    ...Array.from(participants.values())
  ].filter(Boolean);

  const isCurrentUserHost = localParticipant?.isHost || false;

  const handleMuteAll = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Отключить микрофоны',
      message: 'Отключить микрофоны у всех участников (кроме администраторов)?',
      variant: 'warning',
      onConfirm: () => {
        muteAllParticipants();
        toast.success('Микрофоны отключены');
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleEndMeeting = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Завершить конференцию',
      message: 'Завершить конференцию для всех участников? Это действие нельзя отменить.',
      variant: 'danger',
      onConfirm: () => {
        endMeeting();
        toast.success('Конференция завершена');
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleMuteParticipant = (participantId: string, participantName: string) => {
    muteParticipant(participantId);
    toast.success(`Микрофон ${participantName} отключен`);
  };

  const handleMakeAdmin = (participantId: string, participantName: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Назначить администратора',
      message: `Назначить ${participantName} администратором?`,
      variant: 'info',
      onConfirm: () => {
        makeAdmin(participantId);
        toast.success(`${participantName} назначен администратором`);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleKickUser = (participantId: string, participantName: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Исключить участника',
      message: `Исключить ${participantName} из конференции и добавить в черный список?`,
      variant: 'danger',
      onConfirm: () => {
        kickUser(participantId);
        toast.success(`${participantName} исключен из конференции`);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleLowerHand = (participantId: string, participantName: string) => {
    lowerParticipantHand(participantId);
    toast.success(`Рука ${participantName} опущена`);
  };

  return (
    <div className="h-full flex flex-col bg-gray-800">
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            <h3 className="text-white font-semibold">
              Участники ({allParticipants.length})
            </h3>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          )}
        </div>
        {/* Admin Control Buttons */}
        {isCurrentUserHost && (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <button
                onClick={handleMuteAll}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                title="Отключить микрофоны всем участникам"
              >
                <Volume2 className="w-4 h-4" />
                <span className="hidden sm:inline">Выкл. всем микрофоны</span>
              </button>
              <button
                onClick={handleEndMeeting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                title="Завершить конференцию для всех"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Завершить для всех</span>
              </button>
            </div>
            <button
              onClick={() => setBitrateLevel(bitrateLevel === 'standard' ? 'low' : 'standard')}
              className={`w-full px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm ${
                bitrateLevel === 'low' 
                  ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                  : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
              }`}
              title={bitrateLevel === 'low' ? 'Восстановить стандартный битрейт' : 'Понизить битрейт для экономии трафика'}
            >
              <Gauge className="w-4 h-4" />
              <span>{bitrateLevel === 'low' ? 'Восстановить битрейт' : 'Понизить битрейт'}</span>
            </button>
          </div>
        )}
      </div>

      {/* Participants List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {allParticipants.map((participant) => {
          if (!participant) return null;
          
          const isLocal = participant.id === localParticipant?.id;
          const canControl = isCurrentUserHost && !isLocal && !participant.isHost;

          return (
            <div
              key={participant.id}
              className="bg-gray-900 rounded-lg p-3 hover:bg-gray-850 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-semibold text-sm">
                      {participant.displayName.charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {participant.isHost && (
                        <Shield className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      )}
                      <span className="text-white font-medium truncate">
                        {participant.displayName}
                        {isLocal && ' (Вы)'}
                      </span>
                    </div>

                    {/* Status indicators */}
                    <div className="flex items-center gap-2 mt-1">
                      {participant.audioEnabled ? (
                        <Mic className="w-3.5 h-3.5 text-green-400" />
                      ) : (
                        <MicOff className="w-3.5 h-3.5 text-red-400" />
                      )}
                      {participant.videoEnabled ? (
                        <Video className="w-3.5 h-3.5 text-green-400" />
                      ) : (
                        <VideoOff className="w-3.5 h-3.5 text-red-400" />
                      )}
                      {participant.raisedHand && (
                        <div className="flex items-center gap-1 text-yellow-400 text-xs">
                          <Hand className="w-3.5 h-3.5" />
                          <span>Поднял руку</span>
                          {isCurrentUserHost && !isLocal && (
                            <button
                              onClick={() => handleLowerHand(participant.id, participant.displayName)}
                              className="ml-1 p-0.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded transition-colors"
                              title="Опустить руку"
                            >
                              <HandMetal className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions (для host) */}
                {canControl && (
                  <div className="flex items-center gap-1">
                    {/* Mute Button */}
                    {participant.audioEnabled && (
                      <button
                        onClick={() => handleMuteParticipant(participant.id, participant.displayName)}
                        className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                        title="Отключить микрофон"
                      >
                        <MicOff className="w-4 h-4" />
                      </button>
                    )}
                    {/* Make Admin Button */}
                    {!participant.isHost && (
                      <button
                        onClick={() => handleMakeAdmin(participant.id, participant.displayName)}
                        className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-gray-700 rounded-lg transition-colors"
                        title="Назначить администратором"
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                    )}
                    {/* Kick User Button */}
                    <button
                      onClick={() => handleKickUser(participant.id, participant.displayName)}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors"
                      title="Исключить из конференции"
                    >
                      <UserX className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer info */}
      <div className="p-4 border-t border-gray-700">
        <div className="text-xs text-gray-400 text-center">
          {isCurrentUserHost ? (
            'Вы организатор конференции'
          ) : (
            'Вы участник конференции'
          )}
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default ParticipantsList;
